/**
 * PageLayout - Master Layout Component
 * 
 * A reusable component that provides consistent page structure across the application.
 * Use this component to wrap page content for standardized headers, actions, and spacing.
 * 
 * Usage:
 * <PageLayout
 *   title="Page Title"
 *   description="Optional description"
 *   actions={<Button>Add New</Button>}
 *   backLink="/previous-page"
 *   backLabel="Back to List"
 * >
 *   {children}
 * </PageLayout>
 */

import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Loader2 } from 'lucide-react';
import { Button } from '../ui/button';
import { cn } from '../../lib/utils';

export function PageLayout({
  // Page header
  title,
  description,
  badge,
  
  // Actions (right side of header)
  actions,
  
  // Back navigation
  backLink,
  backLabel = 'Back',
  
  // Loading state
  loading = false,
  
  // Content options
  children,
  className,
  
  // Full width (no max-width constraint)
  fullWidth = false,
  
  // No padding (for custom layouts)
  noPadding = false,
}) {
  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-8 h-8 animate-spin text-amber-500" />
      </div>
    );
  }

  return (
    <div className={cn('space-y-6', className)}>
      {/* Back Link */}
      {backLink && (
        <Link 
          to={backLink}
          className="inline-flex items-center gap-2 text-sm text-slate-600 hover:text-slate-900 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          {backLabel}
        </Link>
      )}
      
      {/* Page Header */}
      {(title || actions) && (
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <div className="flex items-center gap-3">
              {title && (
                <h1 className="text-2xl sm:text-3xl font-bold text-slate-900">{title}</h1>
              )}
              {badge}
            </div>
            {description && (
              <p className="text-sm text-slate-500 mt-1">{description}</p>
            )}
          </div>
          {actions && (
            <div className="flex flex-wrap items-center gap-2">
              {actions}
            </div>
          )}
        </div>
      )}
      
      {/* Page Content */}
      <div className={cn(
        !fullWidth && 'max-w-7xl',
        noPadding ? '' : ''
      )}>
        {children}
      </div>
    </div>
  );
}

/**
 * PageSection - Section within a page
 * 
 * Use for grouping related content within a page.
 */
export function PageSection({
  title,
  description,
  actions,
  children,
  className,
}) {
  return (
    <div className={cn('space-y-4', className)}>
      {(title || actions) && (
        <div className="flex items-center justify-between">
          <div>
            {title && (
              <h2 className="text-lg font-semibold text-slate-900">{title}</h2>
            )}
            {description && (
              <p className="text-sm text-slate-500">{description}</p>
            )}
          </div>
          {actions && (
            <div className="flex items-center gap-2">
              {actions}
            </div>
          )}
        </div>
      )}
      {children}
    </div>
  );
}

/**
 * PageCard - Card wrapper for page content
 * 
 * A pre-styled card for consistent content containers.
 */
export function PageCard({
  children,
  className,
  noPadding = false,
}) {
  return (
    <div className={cn(
      'bg-white rounded-lg border border-slate-200 shadow-sm',
      !noPadding && 'p-4 sm:p-6',
      className
    )}>
      {children}
    </div>
  );
}

/**
 * PageHeader - Standalone page header component
 * 
 * Use when you need just the header without the full PageLayout.
 */
export function PageHeader({
  title,
  description,
  badge,
  actions,
  className,
}) {
  return (
    <div className={cn(
      'flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4',
      className
    )}>
      <div>
        <div className="flex items-center gap-3">
          {title && (
            <h1 className="text-2xl sm:text-3xl font-bold text-slate-900">{title}</h1>
          )}
          {badge}
        </div>
        {description && (
          <p className="text-sm text-slate-500 mt-1">{description}</p>
        )}
      </div>
      {actions && (
        <div className="flex flex-wrap items-center gap-2">
          {actions}
        </div>
      )}
    </div>
  );
}

/**
 * EmptyState - Empty state placeholder
 * 
 * Use when there's no data to display.
 */
export function EmptyState({
  icon: Icon,
  title,
  description,
  action,
  className,
}) {
  return (
    <div className={cn(
      'flex flex-col items-center justify-center py-12 px-4 text-center',
      className
    )}>
      {Icon && (
        <div className="w-12 h-12 rounded-full bg-slate-100 flex items-center justify-center mb-4">
          <Icon className="w-6 h-6 text-slate-400" />
        </div>
      )}
      {title && (
        <h3 className="text-lg font-semibold text-slate-900 mb-1">{title}</h3>
      )}
      {description && (
        <p className="text-sm text-slate-500 mb-4 max-w-sm">{description}</p>
      )}
      {action}
    </div>
  );
}

export default PageLayout;
